/**
 * Server timestamps are not all the same shape, and one of the shapes is a
 * trap.
 *
 * SQLite's `datetime('now')` — which backs `expires_at`, `last_login_at`,
 * `deleted_at`, `entered_at` and friends — writes "YYYY-MM-DD HH:MM:SS". That
 * value is UTC, but it carries no zone marker, and `new Date(...)` on a
 * string in that form is specified to be read as LOCAL time. So every such
 * timestamp arrived shifted by the reader's UTC offset: a site sighting with
 * four hours left read as "expired" to someone on UTC+4 and as eight hours to
 * someone on UTC-4. Safari is stricter still and returns Invalid Date, which
 * is how `NaNh NaNm left` reached the screen.
 *
 * Columns declared as real ISO (`esi_director_verified_at`, anything the API
 * builds with toISOString()) already carry a zone and must NOT be stamped
 * again — hence the check rather than an unconditional `+ 'Z'`, which is what
 * the one-off workarounds around the codebase were doing.
 *
 * EVE runs on UTC, so anything a pilot acts on is rendered in UTC too.
 */
export function parseServerDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const isoish = value.includes('T') ? value : value.replace(' ', 'T');
  const withZone = /[Zz]|[+-]\d{2}:?\d{2}$/.test(isoish) ? isoish : `${isoish}Z`;
  const date = new Date(withZone);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Milliseconds from now until `value`, or null if it can't be read. Negative
 * means it already passed. */
export function msUntil(value: string | null | undefined): number | null {
  const date = parseServerDate(value);
  return date ? date.getTime() - Date.now() : null;
}

/** Local-time rendering for bookkeeping timestamps (audit rows, last login),
 * where the reader's own clock is the useful frame. Never "Invalid Date". */
export function formatServerDateTime(value: string | null | undefined, fallback = '—'): string {
  const date = parseServerDate(value);
  return date ? date.toLocaleString() : fallback;
}

/** UTC rendering, labelled, for anything tied to an in-game timer — those are
 * quoted in UTC in-game and in every fleet ping. */
export function formatEveTime(value: string | null | undefined, fallback = '—'): string {
  const date = parseServerDate(value);
  if (!date) return fallback;
  return `${date.toISOString().slice(0, 16).replace('T', ' ')} UTC`;
}
